import { useCallback, useEffect, useRef, useState } from 'react'

export default function usePolledData<T>(request: (signal: AbortSignal) => Promise<T>) {
  const [data, setData] = useState<T>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()
  const [version, setVersion] = useState(0)
  const hasData = useRef(false)
  const reload = useCallback(() => setVersion((value) => value + 1), [])

  useEffect(() => {
    const controller = new AbortController()
    let inFlight = false
    const load = async () => {
      if (inFlight || controller.signal.aborted) return
      inFlight = true
      try {
        const result = await request(controller.signal)
        if (controller.signal.aborted) return
        hasData.current = true
        setError(undefined)
        setData((previous) => JSON.stringify(previous) === JSON.stringify(result) ? previous : result)
      } catch (reason) {
        if (!controller.signal.aborted && !hasData.current) setError(reason)
      } finally {
        inFlight = false
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    load()
    const interval = window.setInterval(load, 10000)
    return () => {
      window.clearInterval(interval)
      controller.abort()
    }
  }, [request, version])

  return { data, loading, error, reload }
}
